import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Typography, Slide, Box, Stack } from '@mui/material';
import { useDictionary, useIsDesktop } from 'Hooks';
import { DELETE_TOAST_DURATION_MS } from 'Config';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDocumentUploadContext } from '../../../../../Context/DocumentUploadContext';

export interface IDeleteToastItem {
  id: string;
  message: string;
}

interface IDeleteToastSingle {
  item: IDeleteToastItem;
  onUndo: (id: string) => void;
  onClose: (id: string) => void;
}

const DeleteToastSingle: FC<IDeleteToastSingle> = ({
  item,
  onUndo,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  const isDesktop = useIsDesktop();
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const onCloseRef = useRef(onClose);
  const itemIdRef = useRef(item.id);

  onCloseRef.current = onClose;
  itemIdRef.current = item.id;

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onCloseRef.current(itemIdRef.current);
    }, 300);
  }, []);

  const handleUndo = useCallback(() => {
    onUndo(item.id);
  }, [onUndo, item.id]);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));

    const timer = setTimeout(handleClose, DELETE_TOAST_DURATION_MS);

    return () => clearTimeout(timer);
  }, [handleClose]);

  return (
    <Slide direction='up' in={isVisible} mountOnEnter unmountOnExit>
      <Alert severity='info' icon={false} sx={{ bgcolor: 'transparent' }}>
        <Stack
          sx={{
            bgcolor: '#F4E4FF',
            color: '#344266',
            borderRadius: '8px',
            p: '20px',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: 'row',
            width: isDesktop ? '680px' : '90vw',
            ml: isDesktop ? '300px' : undefined,
          }}
        >
          <Typography
            variant='body2'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {item.message}
          </Typography>
          <Button
            onClick={handleUndo}
            sx={{
              bgcolor: 'transparent',
            }}
          >
            <Typography
              variant='body2'
              sx={{
                color: '#344266',
                textDecoration: 'underline',
              }}
            >
              {dictionary.uploadFile.undoDelete}
            </Typography>
          </Button>
        </Stack>
      </Alert>
    </Slide>
  );
};

export const DeleteToast = () => {
  const { toastQueue, handleUndoToast, handleCloseToast } =
    useDocumentUploadContext();

  const isDesktop = useIsDesktop();

  if (toastQueue.length === 0) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: isDesktop ? '24px' : '16px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1400,
        display: 'grid',
        justifyItems: 'center',
      }}
    >
      {toastQueue.map((toast) => (
        <DeleteToastSingle
          key={toast.id}
          item={toast}
          onUndo={handleUndoToast}
          onClose={handleCloseToast}
        />
      ))}
    </Box>
  );
};
